import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import { Cursor } from './Cursor';
import {
  DivisionChangeConfigBar_so5Leaderboard,
  DivisionChangeConfigBar_so5Lineup,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 1 })`
  border-radius: var(--double-unit);
  padding: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.07);
  backdrop-filter: blur(32px);
`;
const Bar = styled.div`
  border-radius: var(--unit);
  background-color: var(--c-nd-200);
  position: relative;

  --rgb-promoted: var(--c-rgb-score-high);
  --rgb-relegated: var(--c-rgb-rivals-loss);
  --rgb-stable: 155, 155, 155;
`;
const Sections = styled(Horizontal).attrs({ gap: 0 })`
  position: relative;
`;
const Section = styled.div`
  height: calc(5 * var(--unit));

  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: var(--half-unit);

  color: rgba(var(--c-rgb-black), 0.5);

  &:first-of-type {
    border-top-left-radius: var(--unit);
    border-bottom-left-radius: var(--unit);
  }
  &:last-of-type {
    border-top-right-radius: var(--unit);
    border-bottom-right-radius: var(--unit);
  }

  &.center {
    justify-content: center;
    align-items: center;
  }
`;
const RelegatedSection = styled(Section)`
  background-color: rgba(var(--rgb-relegated), 0.5);
`;
const StableSection = styled(Section)`
  flex: 1;
  align-items: end;
  justify-content: space-between;
  color: var(--c-nd-400);
  background-color: rgba(var(--rgb-stable), 0.1);
`;
const PromotedSection = styled(Section)`
  background-color: rgba(var(--rgb-promoted), 0.5);
`;
const StyledCursor = styled(Cursor)`
  position: absolute;
  top: calc(-1 * var(--intermediate-unit));
  transform: translateX(50%);
`;
const StyledHorizontal = styled(Horizontal)`
  flex: 1;
`;

const HUNDRED_BASIS_POINT = 10000;

type Props = {
  so5Leaderboard: DivisionChangeConfigBar_so5Leaderboard;
  so5Lineup?: Nullable<DivisionChangeConfigBar_so5Lineup>;
};

export const DivisionChangeConfigBar = ({
  so5Leaderboard,
  so5Lineup,
}: Props) => {
  const { divisionChangeConfiguration, so5LineupsCount } = so5Leaderboard;
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);
  const isStarted = so5Lineup ? hasGamesStarted(so5Lineup) : !isUpcoming;

  if (!divisionChangeConfiguration) {
    return null;
  }

  const {
    lastPromotedSo5Ranking,
    lastStableSo5Ranking,
    promotionRankBasisPoint,
    relegationRankBasisPoint,
  } = divisionChangeConfiguration;

  const so5Ranking = so5Lineup?.so5Rankings[0];

  return (
    <Root>
      <Bar>
        <Sections>
          {relegationRankBasisPoint > 0 && (
            <RelegatedSection
              className={classNames({ center: isStarted })}
              style={{
                width: `${relegationRankBasisPoint / 100}%`,
              }}
            >
              {!isStarted && (
                <LabelS as="p" bold>
                  <FormattedNumber
                    value={relegationRankBasisPoint / HUNDRED_BASIS_POINT}
                    maximumFractionDigits={0}
                    style="percent"
                  />
                </LabelS>
              )}
            </RelegatedSection>
          )}
          <StableSection
            className={classNames({
              center: !lastStableSo5Ranking && !lastPromotedSo5Ranking,
            })}
          >
            <LabelS as="p" bold>
              {lastStableSo5Ranking ? (
                <FormattedMessage
                  {...fantasy.pointsWithStyledScoreWithoutFormat}
                  values={{
                    score: Math.floor(lastStableSo5Ranking?.score),
                    span: (...chunks) => <span>{chunks}</span>,
                  }}
                />
              ) : (
                <FormattedNumber
                  value={
                    (HUNDRED_BASIS_POINT -
                      promotionRankBasisPoint -
                      relegationRankBasisPoint) /
                    HUNDRED_BASIS_POINT
                  }
                  maximumFractionDigits={0}
                  style="percent"
                />
              )}
            </LabelS>
            {lastPromotedSo5Ranking && (
              <LabelS as="p" bold>
                <FormattedMessage
                  {...fantasy.pointsWithStyledScoreWithoutFormat}
                  values={{
                    score: Math.floor(lastPromotedSo5Ranking?.score),
                    span: (...chunks) => <span>{chunks}</span>,
                  }}
                />
              </LabelS>
            )}
          </StableSection>
          {promotionRankBasisPoint > 0 && (
            <PromotedSection
              style={{
                width: `${promotionRankBasisPoint / 100}%`,
              }}
            >
              <LabelS as="p" bold>
                {!lastPromotedSo5Ranking && (
                  <FormattedNumber
                    value={promotionRankBasisPoint / HUNDRED_BASIS_POINT}
                    maximumFractionDigits={0}
                    style="percent"
                  />
                )}
              </LabelS>
            </PromotedSection>
          )}
        </Sections>
        {so5Ranking?.ranking && so5Ranking.eligibleDivisionChange && (
          <StyledCursor
            color={`rgb(var(--rgb-${so5Ranking.eligibleDivisionChange?.toLowerCase()}))`}
            style={{
              right: `${
                ((so5Ranking.ranking - 1) / (so5LineupsCount - 1)) * 100
              }%`,
            }}
          />
        )}
      </Bar>
      <Sections>
        {relegationRankBasisPoint > 0 && (
          <Horizontal
            center
            style={{
              width: `${relegationRankBasisPoint / 100}%`,
            }}
          >
            <LabelS as="p" color="var(--c-nd-400)">
              <FormattedMessage {...fantasy.relegation} />
            </LabelS>
          </Horizontal>
        )}
        <StyledHorizontal center>
          <LabelS as="p" color="var(--c-nd-400)">
            <FormattedMessage {...fantasy.safeZone} />
          </LabelS>
        </StyledHorizontal>
        {promotionRankBasisPoint > 0 && (
          <Horizontal
            center
            style={{
              width: `${promotionRankBasisPoint / 100}%`,
            }}
          >
            <LabelS as="p" color="var(--c-nd-400)">
              <FormattedMessage {...fantasy.promotion} />
            </LabelS>
          </Horizontal>
        )}
      </Sections>
    </Root>
  );
};

DivisionChangeConfigBar.fragments = {
  so5Leaderboard: gql`
    fragment DivisionChangeConfigBar_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      so5Fixture {
        slug
        aasmState
      }
      divisionChangeConfiguration {
        id
        lastPromotedSo5Ranking {
          id
          score
        }
        lastStableSo5Ranking {
          id
          score
        }
        promotionRankBasisPoint
        relegationRankBasisPoint
      }
    }
  ` as TypedDocumentNode<DivisionChangeConfigBar_so5Leaderboard>,
  so5Lineup: gql`
    fragment DivisionChangeConfigBar_so5Lineup on So5Lineup {
      id
      so5Rankings {
        id
        ranking
        eligibleDivisionChange
      }
      ...hasGamesStarted_so5Lineup
    }
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<DivisionChangeConfigBar_so5Lineup>,
};
